import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonIcon from "@mui/icons-material/Person";
import FolderIcon from "@mui/icons-material/Folder";
import { getSOWDetails, getLinkedProjectsListBySOWId, getSOWAllocationProjection } from "../../http/requests/sow";
import "./SOWAllocationsView.css";

const MONTH_LABELS = {
  "01": "Jan", "02": "Feb", "03": "Mar", "04": "Apr", "05": "May", "06": "Jun",
  "07": "Jul", "08": "Aug", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dec",
};

const formatMonthLabel = (monthKey) => {
  if (!monthKey || monthKey.length < 7) return monthKey;
  const [y, m] = monthKey.split("-");
  return `${MONTH_LABELS[m] || m} ${y}`;
};

const formatSOWDate = (d) => {
  if (d == null) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const CHART_VIEW_BAR = "bar";
const CHART_VIEW_LINE = "line";

export default function SOWAllocationsView() {
  const [sowList, setSowList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [sowId, setSowId] = useState("");
  const [projectId, setProjectId] = useState(0);
  const [data, setData] = useState(null);
  const [loadingSows, setLoadingSows] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [chartView, setChartView] = useState(CHART_VIEW_BAR);

  useEffect(() => {
    let cancelled = false;
    setLoadingSows(true);
    getSOWDetails(1, 500, "asc", "SOWName", "")
      .then((res) => {
        if (cancelled) return;
        const list = res?.data ?? res ?? [];
        setSowList(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setSowList([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSows(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!sowId || sowId === "") {
      setProjects([]);
      setProjectId(0);
      return;
    }
    let cancelled = false;
    setLoadingProjects(true);
    setProjects([]);
    setProjectId(0);
    getLinkedProjectsListBySOWId(sowId)
      .then((res) => {
        if (cancelled) return;
        const list = res?.data ?? res ?? [];
        setProjects(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingProjects(false);
      });
    return () => { cancelled = true; };
  }, [sowId]);

  const handleViewData = useCallback(() => {
    if (!sowId || sowId === "") {
      setError("Please select an SOW.");
      return;
    }
    setError(null);
    setLoadingData(true);
    setData(null);
    const pid = projectId === "" || projectId == null ? null : Number(projectId);
    getSOWAllocationProjection(Number(sowId), pid === 0 ? null : pid)
      .then((res) => {
        const payload = res?.data ?? res;
        setData(payload || null);
      })
      .catch((err) => {
        setError(err?.response?.data?.errorMessage || err?.message || "Failed to load projection data.");
        setData(null);
      })
      .finally(() => setLoadingData(false));
  }, [sowId, projectId]);

  const chartData = useMemo(
    () =>
      data?.series?.map((s) => {
        const counts = s.projectCounts ?? s.ProjectCounts ?? [];
        return {
          month: s.month,
          monthLabel: formatMonthLabel(s.month),
          proposed: Number(s.proposed),
          actual: Number(s.actual),
          variance: Number(s.variance ?? (s.actual - s.proposed)),
          projectCounts: counts.map((pc) => ({
            projectName: pc.projectName ?? pc.ProjectName ?? "",
            count: Number(pc.count ?? pc.Count ?? 0),
          })),
        };
      }) ?? [],
    [data?.series]
  );

  const sowName = data?.sowName ?? data?.SowName ?? "";

  const CustomTooltip = useCallback(
    ({ active, payload, label }) => {
      if (!active || !payload?.length || !label) return null;
      const point = payload[0]?.payload;
      const projectCounts = point?.projectCounts ?? [];
      return (
        <Paper elevation={3} sx={{ p: 1.5, minWidth: 200, border: "1px solid #e0e0e0" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#1773bc", mb: 0.5 }}>
            {label}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
            SOW: {sowName || "—"}
          </Typography>
          {projectCounts.length > 0 ? (
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.25 }}>
                Projects (allocation count):
              </Typography>
              {projectCounts.map((pc, i) => (
                <Typography key={i} variant="caption" display="block" sx={{ pl: 1 }}>
                  • {pc.projectName || "—"} ({pc.count})
                </Typography>
              ))}
            </Box>
          ) : null}
          {payload.map((entry) => (
            <Typography key={entry.dataKey} variant="body2" sx={{ color: entry.color }}>
              {entry.name}: {Number(entry.value).toFixed(2)}
            </Typography>
          ))}
        </Paper>
      );
    },
    [sowName]
  );

  return (
    <div className="sow-allocations-view">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ color: "#1a237e", fontWeight: 700, letterSpacing: "-0.02em" }}>
          SOW Allocations
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Proposed vs actual allocations (month-wise)
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, borderColor: "#e0e0e0" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
          <FormControl sx={{ minWidth: 440 }} size="small">
            <InputLabel id="sow-select-label">SOW</InputLabel>
            <Select
              labelId="sow-select-label"
              value={sowId}
              label="SOW"
              onChange={(e) => setSowId(e.target.value)}
              disabled={loadingSows}
            >
              <MenuItem value="">
                <em>Select SOW</em>
              </MenuItem>
              {sowList.map((s) => (
                <MenuItem key={s.sowId ?? s.SOWId} value={s.sowId ?? s.SOWId}>
                  {s.sowName ?? s.SOWName ?? s.sowId ?? s.SOWId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 440 }} size="small">
            <InputLabel id="project-select-label">Project</InputLabel>
            <Select
              labelId="project-select-label"
              value={projectId}
              label="Project"
              onChange={(e) => setProjectId(e.target.value === "all" ? 0 : e.target.value)}
              disabled={loadingProjects || !sowId}
            >
              <MenuItem value={0}>ALL</MenuItem>
              {projects.map((p) => (
                <MenuItem key={p.projectId ?? p.ProjectId} value={p.projectId ?? p.ProjectId}>
                  {p.projectName ?? p.ProjectName ?? p.projectId ?? p.ProjectId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleViewData}
            disabled={loadingData || !sowId}
            sx={{ backgroundColor: "#3949ab", "&:hover": { backgroundColor: "#303f9f" } }}
          >
            {loadingData ? "Loading…" : "View Data"}
          </Button>
          <Button
            variant="outlined"
            onClick={() => { setData(null); setError(null); }}
            disabled={!data && !error}
            sx={{ borderColor: "#3949ab", color: "#3949ab" }}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data && (
        <>
          <Card className="sow-info-card" elevation={0} sx={{ mb: 3 }}>
            <CardContent sx={{ "&:last-child": { pb: 2 } }}>
              <Typography variant="overline" sx={{ color: "#1773bc", fontWeight: 600, letterSpacing: 1 }}>
                SOW details
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#1a237e", mt: 0.5, mb: 2 }}>
                {data.sowName ?? data.SowName}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalendarMonthIcon sx={{ color: "#5c6bc0", fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Dates</Typography>
                    <Typography variant="body2">
                      {formatSOWDate(data.startDate ?? data.StartDate)} – {formatSOWDate(data.endDate ?? data.EndDate)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PersonIcon sx={{ color: "#5c6bc0", fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Account Manager</Typography>
                    <Typography variant="body2">{(data.accountManagerName ?? data.AccountManagerName) || "—"}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PersonIcon sx={{ color: "#5c6bc0", fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Delivery Manager</Typography>
                    <Typography variant="body2">{(data.deliveryManagerName ?? data.DeliveryManagerName) || "—"}</Typography>
                  </Box>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <FolderIcon sx={{ color: "#5c6bc0", fontSize: 20, mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">Linked projects</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
                    {(data.projects ?? data.Projects ?? []).length > 0
                      ? (data.projects ?? data.Projects).map((p) => (
                          <Chip
                            key={p.projectId ?? p.ProjectId}
                            label={p.projectName ?? p.ProjectName}
                            size="small"
                            sx={{ backgroundColor: "#e8eaf6", color: "#3949ab", fontWeight: 500 }}
                          />
                        ))
                      : "—"}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <div className="sow-allocations-chart">
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1 }}>
              <Typography variant="h6" sx={{ color: "#333" }}>
                SOW Proposed vs Actual Allocations (Month-wise)
              </Typography>
              <ToggleButtonGroup
                value={chartView}
                exclusive
                onChange={(_, v) => v != null && setChartView(v)}
                size="small"
                sx={{ "& .MuiToggleButton-root": { py: 0.5 } }}
              >
                <ToggleButton value={CHART_VIEW_BAR} aria-label="Bar chart">
                  <BarChartIcon sx={{ mr: 0.5 }} /> Bar
                </ToggleButton>
                <ToggleButton value={CHART_VIEW_LINE} aria-label="Line chart">
                  <ShowChartIcon sx={{ mr: 0.5 }} /> Line
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={380}>
                {chartView === CHART_VIEW_LINE ? (
                  <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="monthLabel"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                    />
                    <YAxis label={{ value: "Allocation", angle: -90, position: "insideLeft" }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="proposed"
                      name="Proposed (SOW)"
                      stroke="#1773bc"
                      strokeWidth={2}
                      dot={{ r: 4, fill: "#1773bc" }}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="actual"
                      name="Actual (Projects)"
                      stroke="#2e7d32"
                      strokeWidth={2}
                      dot={{ r: 4, fill: "#2e7d32" }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                ) : (
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="monthLabel"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                    />
                    <YAxis label={{ value: "Allocation", angle: -90, position: "insideLeft" }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="proposed" name="Proposed (SOW)" fill="#1773bc" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="actual" name="Actual (Projects)" fill="#2e7d32" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary">No series data to display.</Typography>
            )}
          </div>

          <TableContainer component={Paper} sx={{ mt: 3, maxHeight: 400, borderRadius: 2, overflow: "hidden", border: "1px solid #eee" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f7ff" }}>
                  <TableCell sx={{ fontWeight: 600, color: "#3949ab" }}>Month</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: "#3949ab" }}>Proposed</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: "#3949ab" }}>Actual</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: "#3949ab" }}>Variance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {chartData.map((row) => (
                  <TableRow key={row.month}>
                    <TableCell>{row.monthLabel}</TableCell>
                    <TableCell align="right">{row.proposed}</TableCell>
                    <TableCell align="right">{row.actual}</TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 600,
                        color: row.variance > 0 ? "#c62828" : row.variance < 0 ? "#2e7d32" : "text.secondary",
                        backgroundColor: row.variance > 0 ? "rgba(198, 40, 40, 0.08)" : row.variance < 0 ? "rgba(46, 125, 50, 0.08)" : "inherit",
                      }}
                    >
                      {row.variance > 0 ? "+" : ""}{row.variance}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!data && !loadingData && !error && sowId && (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          Click &quot;View Data&quot; to load the chart and table.
        </Typography>
      )}
    </div>
  );
}
